import { useState, useEffect, useCallback, useRef } from 'react';

interface UseTypingEffectOptions {
  /**
   * Speed of typing in milliseconds per character
   * @default 50
   */
  speed?: number;

  /**
   * Callback when typing animation completes
   */
  onComplete?: () => void;

  /**
   * Whether to start typing immediately
   * @default true
   */
  autoStart?: boolean;
}

interface UseTypingEffectResult {
  /**
   * The currently displayed text (being typed out)
   */
  displayedText: string;

  /**
   * Whether the typing animation is complete
   */
  isComplete: boolean;

  /**
   * Whether the typing animation is currently active
   */
  isTyping: boolean;

  /**
   * Start or restart the typing animation
   */
  start: () => void;

  /**
   * Skip to the end of the animation
   */
  skip: () => void;

  /**
   * Reset the animation
   */
  reset: () => void;
}

/**
 * Hook for creating a typing animation effect
 *
 * @example
 * ```tsx
 * const { displayedText, isComplete } = useTypingEffect("Hello World", {
 *   speed: 50,
 *   onComplete: () => console.log("Done!"),
 * });
 * ```
 */
export function useTypingEffect(
  text: string,
  options: UseTypingEffectOptions = {}
): UseTypingEffectResult {
  const { speed = 50, onComplete, autoStart = true } = options;

  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [shouldStart, setShouldStart] = useState(autoStart);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);

  // Keep onComplete ref up to date
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Reset when text changes
  useEffect(() => {
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
    setIsTyping(false);
    setShouldStart(autoStart);
  }, [text, autoStart]);

  // Typing animation
  useEffect(() => {
    if (!shouldStart || !text || currentIndex >= text.length) {
      if (currentIndex >= text.length && text.length > 0 && !isComplete) {
        setIsComplete(true);
        setIsTyping(false);
        onCompleteRef.current?.();
      }
      return;
    }

    setIsTyping(true);

    timeoutRef.current = setTimeout(() => {
      setDisplayedText((prev) => prev + text[currentIndex]);
      setCurrentIndex((prev) => prev + 1);
    }, speed);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [text, currentIndex, speed, shouldStart, isComplete]);

  const start = useCallback(() => {
    if (!isComplete) {
      setShouldStart(true);
    }
  }, [isComplete]);

  const skip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setDisplayedText(text);
    setCurrentIndex(text.length);
    setIsComplete(true);
    setIsTyping(false);
    onCompleteRef.current?.();
  }, [text]);

  const reset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setDisplayedText('');
    setCurrentIndex(0);
    setIsComplete(false);
    setIsTyping(false);
    setShouldStart(false);
  }, []);

  return {
    displayedText,
    isComplete,
    isTyping,
    start,
    skip,
    reset,
  };
}
